import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { NavLink, useLocation, useHistory } from 'react-router-dom';
import { LoadingStatus } from '@paycor/design-system';
import { Security } from 'paycor-react-oidc';

import config from 'config';
import { ROUTE_URLS } from 'constants/index';
import { redirectToLogout, clearUser } from 'utils/authentication';
import useSetLoggedInUser from 'hooks/useSetLoggedInUser';
import { useLoggedInUserDispatchContext, useLoggedInUserStateContext, useSetMeDetails, useMeDetailsFromContext } from 'context/loggedin-user-context';
import useRegisterPendoMetadata from 'hooks/useRegisterPendoMetadata';
import TermsAndConditionsModal from 'features/AppCreator/_components/Applications/TermsAndConditionsModal';
import { Select } from '@paycor/pds-form-elements';

import logo from 'images/Paycor-DeveloperPortal.svg';
import oldLogo from 'images/paycor_developer_portal.svg';
import styles from './_styles.module.scss';
import { useTranslation } from 'react-i18next';
import ApiExplorerTabList from 'features/Documentation/components/apiExplorerTabList/ApiExplorerTabList';

// Lazy load chat button to prevent context from being initialized on every page load
const ChatAssistantButton = lazy(() => import('components/ChatAssistantButton'));

const Header = ({ onVersionChange, onBannerHeightChange }) => {
	const { t } = useTranslation();
	useSetMeDetails(); // Initialize meDetails in context
	const { meDetails } = useMeDetailsFromContext();

	const {
		HOST: { BASE_DOMAIN_URL, HCM_BASE_DOMAIN_URL },
		IS_PRODUCTION,
		USE_NEW_LOGO_FLAG
	} = config;
	const { APP_CREATOR, DEV_PORTAL } = ROUTE_URLS;
	let location = useLocation();
	let history = useHistory();
	const dispatch = useLoggedInUserDispatchContext();
	const state = useLoggedInUserStateContext();
	const isUserLoggedIn = Boolean(state) && Boolean(state.user);

	const apiVersions = [
		{ value: '1', label: 'Version 1' },
		{ value: '2', label: 'Version 2' }
	];

	const [selectedVersion, setSelectedVersion] = useState(apiVersions[0].value);

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const version = queryParams.get('version') || '1';
		setSelectedVersion(version);
		onVersionChange(version);
	}, [location.search, onVersionChange]);

	const handleVersionChange = useCallback(
		(selectedOption) => {
			const queryParams = new URLSearchParams(location.search);
			setSelectedVersion(selectedOption.value);
			onVersionChange(selectedOption.value);
			queryParams.set('version', selectedOption.value);
			history.push({ search: queryParams.toString() });
		},
		[location.search, onVersionChange, history]
	);

	useSetLoggedInUser();
	useRegisterPendoMetadata(isUserLoggedIn, process.env.NODE_ENV);

	const showAppcreatorLink = () => {
		if (typeof meDetails === 'undefined' || meDetails === null) return false;
		if (meDetails.hasEntities) return true;
		return false;
	};

	const getReturnUrl = () => {
		return encodeURI(location.pathname);
	};

	const apiVersionSelector = (
		<div className={styles['select-api-version']}>
			<Select
				className={styles['select-dropdown-button']}
				size={Select.Sizes.SMALL}
				options={apiVersions}
				placeholder={[t('version'), selectedVersion].join(' ')}
				value={apiVersions.find((option) => option.value === selectedVersion)}
				onChange={handleVersionChange}
			/>
		</div>
	);

	// Calculate dynamic banner height for margin
	const isApiExplore = location.pathname === DEV_PORTAL.API_EXPLORE;
	const bannerHeight = isApiExplore ? 90 : 50;

	// Notify parent of banner height for layout
	useEffect(() => {
		if (onBannerHeightChange) {
			onBannerHeightChange(bannerHeight);
		}
	}, [bannerHeight, onBannerHeightChange]);
	// Add a class or style to the main content to offset the header/banner


	return (
		<Security
			settings={config.securitySettings}
			onUnAuthorizedCallback={() => {}}
			onAuthErrorCallback={() => {}}
			consoleLogsEnabled={false}
			loaderComponent={<LoadingStatus />}
		>
			<TermsAndConditionsModal id="terms-and-condition-modal"></TermsAndConditionsModal>
			<header-container id="nav-header">
				<header-row>
					<left-menu-section>
						<menu-logo>
							<NavLink to="/">
								<img className="content picture picture-default" src={USE_NEW_LOGO_FLAG ? logo : oldLogo} alt="logo" title={t('paycor_developer_portal')} />
							</NavLink>
						</menu-logo>
					</left-menu-section>
					<right-menu-section>
						<nav className={styles['menu']}>
							<menu-horizontal>
								<ul className={styles['nav']}>
									<li className={styles['nav-item']}>
										<NavLink to={DEV_PORTAL.API_EXPLORE} className={styles['nav-link']}>
											{t('header_apis')}
										</NavLink>
									</li>
									<li className={styles['nav-item']}>
										<NavLink to={DEV_PORTAL.GUIDES} className={styles['nav-link']}>
											{t('header_guides')}
										</NavLink>
									</li>
									{isUserLoggedIn && (
										<li className={styles['nav-item']}>
											<NavLink to={DEV_PORTAL.SUPPORT} className={styles['nav-link']}>
												{t('header_support')}
											</NavLink>
										</li>
									)}
									{showAppcreatorLink() && isUserLoggedIn && (
										<li className={styles['nav-item']}>
											<NavLink to={APP_CREATOR.SUMMARY} className={styles['nav-link']}>
												{t('header_applications')}
											</NavLink>
										</li>
									)}
									{isUserLoggedIn && (
										<li className={styles['nav-item']}>
											<NavLink to={DEV_PORTAL.NEWS} className={styles['nav-link']}>
												{t('header_whats_new')}
											</NavLink>
										</li>
									)}
									<li className={styles['nav-item']}>
										<div className={styles['chat-assistant-nav']}>
											<Suspense fallback={<div style={{ width: '200px', height: '36px' }}></div>}>
												<ChatAssistantButton />
											</Suspense>
										</div>
									</li>
									<li className={styles['nav-item']}>
										{isUserLoggedIn ? (
											<a
												href="#"
												onClick={() => {
													redirectToLogout();
													clearUser(dispatch);
												}}
												className={styles['nav-link']}
											>
												{t('header_sign_out')}
											</a>
										) : (
											<a href={`${HCM_BASE_DOMAIN_URL}/accounts/Authentication/Signin?ReturnUrl=${BASE_DOMAIN_URL}${getReturnUrl()}`} className={styles['nav-link']}>
												{t('header_sign_in')}
											</a>
										)}
									</li>
								</ul>
							</menu-horizontal>
						</nav>
					</right-menu-section>
				</header-row>
			</header-container>
				{IS_PRODUCTION && (
					<production-banner style={{ height: `${bannerHeight}px` }}>
						{isApiExplore ? (
							<div>
								<div className={styles['env-name']}>
									{t('explore_by_product')} ({t('production_environment')})
								</div>
								<ApiExplorerTabList selectedVersion={selectedVersion} />
							</div>
						) : (
							<div className={styles['env-name']}>{t('production_environment')}</div>
						)}
						{(isApiExplore || location.pathname === DEV_PORTAL.API_TRY) && <>{apiVersionSelector}</>}
					</production-banner>
				)}

				{!IS_PRODUCTION && (
					<sandbox-banner style={{ height: `${bannerHeight}px` }}>
						{isApiExplore ? (
							<div>
								<div className={styles['env-name']}>
									{t('explore_by_product')} ({t('sandbox_environment')})
								</div>
								<ApiExplorerTabList selectedVersion={selectedVersion} />
							</div>
						) : (
							<div className={styles['env-name']}>{t('sandbox_environment')}</div>
						)}
						{(isApiExplore || location.pathname === DEV_PORTAL.API_TRY) && <>{apiVersionSelector}</>}
					</sandbox-banner>
				)}
		</Security>
	);
};

export default Header;
